import { CheckCircle2, Clock } from "lucide-react";
import { SITE_URL } from "@/components/Seo";

export const SHIPPING_FEE_PIECES = 9.9;
export const SHIPPING_FREE_THRESHOLD_PIECES = 100;

export const STOCK_LABEL = {
  in_stock: { label: "En stock", color: "#16A34A", bg: "#DCFCE7", icon: CheckCircle2, schema: "https://schema.org/InStock" },
  on_order: { label: "Sur commande", color: "#EA580C", bg: "#FFEDD5", icon: Clock, schema: "https://schema.org/BackOrder" },
  out: { label: "Rupture", color: "#DC2626", bg: "#FEE2E2", icon: Clock, schema: "https://schema.org/OutOfStock" },
};

export const piecePath = (piece) => `/pieces-detachees/${piece.slug}`;

export const pieceMatchesMoteur = (p, slug) => p.moteur_slug === slug || (p.moteurs_compatibles || []).includes(slug);

export const pieceMoteurNoms = (p) => (p.moteurs?.length ? p.moteurs.map((m) => m.nom) : [p.moteur_nom]);

export const pieceRefsOrigine = (p) => (p.refs_origine || []).filter((r) => r.reference);

export const pieceToCartItem = (piece, qty = 1) => ({
  slug: piece.slug,
  type: "piece",
  nom: `${piece.nom}${piece.reference ? ` — Réf. ${piece.reference}` : ""}`,
  prix_ht: piece.prix_ht,
  prix_ttc: piece.prix_ttc,
  prix: piece.prix_ttc || piece.prix_ht,
  prix_unite: "TTC",
  image: piece.image,
  qty,
});

const clip = (s, n) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);

export function pieceSeoTitle(p) {
  if (p.seo_title) return p.seo_title;
  return clip(`${p.nom}${p.reference ? ` – Réf. ${p.reference}` : ""}`, 60);
}

export function pieceSeoDescription(p) {
  if (p.seo_description) return p.seo_description;
  const prix = p.prix_ttc ? ` ${String(p.prix_ttc).replace(".", ",")} € TTC.` : "";
  const oem = pieceRefsOrigine(p).slice(0, 2).map((r) => `${r.marque || ""} ${r.reference}`.trim()).join(", ");
  return clip(
    `${p.nom}${p.reference ? ` (réf. ${p.reference})` : ""}, compatible ${pieceMoteurNoms(p).join(", ")}.${oem ? ` Équivalent réf. origine ${oem}.` : ""}${prix} Livraison 9,90 €, offerte dès 100 €.`,
    158,
  );
}

export function pieceJsonLd(p) {
  const url = `${SITE_URL}${piecePath(p)}`;
  const stock = STOCK_LABEL[p.stock] || STOCK_LABEL.in_stock;
  const product = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${url}#product`,
    name: p.nom,
    description: p.description_longue || p.description || pieceSeoDescription(p),
    sku: p.reference || p.slug,
    mpn: pieceRefsOrigine(p)[0]?.reference || p.reference || undefined,
    additionalProperty: pieceRefsOrigine(p).map((r) => ({ "@type": "PropertyValue", name: `Référence origine ${r.marque || ""}`.trim(), value: r.reference })),
    category: [p.categorie_nom, p.sous_categorie].filter(Boolean).join(" > "),
    image: p.image ? [p.image] : [`${SITE_URL}/logo.png`],
    url,
    isAccessoryOrSparePartFor: [
      ...pieceMoteurNoms(p).map((n) => ({ "@type": "Product", name: `Moteur ${n}` })),
      ...(p.marques || []).map((m) => ({ "@type": "Product", name: `Mini-pelle ${m}`, brand: { "@type": "Brand", name: m } })),
    ],
    offers: {
      "@type": "Offer",
      url,
      priceCurrency: "EUR",
      price: Number(p.prix_ttc || 0).toFixed(2),
      priceValidUntil: `${new Date().getFullYear() + 1}-12-31`,
      availability: stock.schema,
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: "LMB Services – La Minipelle Berjalienne", url: SITE_URL },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: {
          "@type": "MonetaryAmount",
          value: Number(p.prix_ttc || 0) >= SHIPPING_FREE_THRESHOLD_PIECES ? "0" : SHIPPING_FEE_PIECES.toFixed(2),
          currency: "EUR",
        },
        shippingDestination: { "@type": "DefinedRegion", addressCountry: "FR" },
      },
    },
  };
  const crumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Pièces détachées", item: `${SITE_URL}/pieces-detachees` },
      { "@type": "ListItem", position: 3, name: p.moteur_nom, item: `${SITE_URL}/pieces-detachees?moteur=${p.moteur_slug}` },
      { "@type": "ListItem", position: 4, name: p.nom, item: url },
    ],
  };
  return [product, crumbs];
}
